import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { Download } from "lucide-react";

export const SpecTable = () => {
  const { t, lang } = useLang();
  const s = t.specs;
  const models = [s.m1, s.m2, s.m3];
  const rows = [
    { k: "voltage", label: s.rows.voltage },
    { k: "power", label: s.rows.power },
    { k: "equiv", label: s.rows.equiv },
    { k: "rpm", label: s.rows.rpm },
    { k: "cooling", label: s.rows.cooling },
    { k: "control", label: s.rows.control },
    { k: "weight", label: s.rows.weight },
  ];

  return (
    <section id="specs" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="mono-label">{s.eyebrow}</p>
          <h2 className="mt-4 font-display uppercase font-bold tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl max-w-3xl">
            {s.heading}
          </h2>
          <p className="mt-6 max-w-2xl text-txt-2 leading-relaxed">{s.lead}</p>
        </Reveal>

        {/* desktop table */}
        <Reveal delay={0.1} className="mt-12 hidden md:block">
          <div className="overflow-x-auto card-surface" data-testid="spec-table">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-line">
                  <th className="p-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-txt-3 font-normal">{s.rows.model}</th>
                  {models.map((m) => (
                    <th key={m.name} className="p-5">
                      <span className="font-display uppercase font-bold text-2xl text-hadron-cyan">{m.name.split("·")[0].trim()}</span>
                      <span className="block mt-1 font-mono2 text-[10px] uppercase tracking-[0.18em] text-txt-3">{m.equiv}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.k} className="border-b border-line last:border-0 hover:bg-hadron-cyan/5 transition-colors">
                    <td className="p-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-txt-3">{r.label}</td>
                    {models.map((m) => (
                      <td key={m.name} className="p-5 text-txt text-sm lg:text-base" data-label={r.label}>
                        {m[r.k]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* mobile cards */}
        <div className="mt-12 md:hidden spec-table" data-testid="spec-cards">
          {models.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.08}>
              <table className="w-full">
                <thead>
                  <tr><th className="p-4 bg-ink-2"><span className="font-display uppercase font-bold text-2xl text-hadron-cyan">{m.name}</span></th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.k}>
                      <td data-label={r.label} className="p-4 text-txt text-sm">{m[r.k]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-8 text-sm text-txt-3 max-w-2xl" data-testid="specs-note">{s.note}</p>
          <a
            data-testid="specs-datasheet"
            href={(lang === "en" ? "" : "/" + lang) && ("/datasheets" + ({ pl: "pl", de: "de", fr: "fr", it: "it", hr: "hr" }[lang] ? "/" + { pl: "pl", de: "de", fr: "fr", it: "it", hr: "hr" }[lang] : "") + "/electric-outboards.pdf")}
            download
            className="mt-5 inline-flex items-center gap-2.5 border border-hadron-cyan/60 px-5 py-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-hadron-cyan hover:bg-hadron-cyan hover:text-ink transition-colors"
          >
            <Download size={13} />
            {s.datasheet}
          </a>
        </Reveal>

        {/* market momentum band */}
        <Reveal delay={0.2} className="mt-10">
          <p className="mono-label">{s.marketsTitle}</p>
          <div className="mt-5 grid sm:grid-cols-3 gap-px bg-line border border-line" data-testid="market-momentum">
            {s.markets.map((m, i) => (
              <div key={i} className="bg-ink p-6 lg:p-8" data-testid={`market-stat-${i}`}>
                <p className="font-mono2 text-2xl lg:text-3xl text-hadron-cyan">{m.v}</p>
                <p className="mt-2.5 text-xs lg:text-sm text-txt-2 leading-relaxed">{m.l}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
